<template>
  <div class="app-container approval-category">
    <!-- 顶部操作区 -->
    <div class="top-bar">
      <el-button type="primary" icon="el-icon-edit-outline" @click="handleStartFlow">发起审批</el-button>
      <div class="filter-area">
        <el-date-picker
          v-model="createTimeRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          format="yyyy-MM-dd"
          value-format="timestamp"
          :picker-options="createTimePickerOptions"
          clearable
          style="width: 260px;"
        />
        <el-input v-model="keyword" placeholder="搜索流程标题" style="width: 220px;" clearable @keyup.enter.native="handleSearch" />
        <el-button icon="el-icon-search" @click="handleSearch">搜索</el-button>
      </div>
    </div>

    <!-- Tab 分类 -->
    <el-tabs v-model="activeTab" type="border-card" @tab-click="handleTabChange">
        <el-tab-pane name="todo">
          <span slot="label" class="tab-label-with-badge">
            <i class="el-icon-bell"></i> 待我审批
            <el-badge v-if="todoCount > 0" :value="todoCount" :max="99" class="approval-todo-badge" />
          </span>
        </el-tab-pane>
      <el-tab-pane name="done">
        <span slot="label"><i class="el-icon-finished"></i> 我已审批</span>
      </el-tab-pane>
      <el-tab-pane name="mine">
        <span slot="label"><i class="el-icon-s-promotion"></i> 我发起的</span>
      </el-tab-pane>
    </el-tabs>

    <!-- 列表 -->
    <el-table v-loading="loading" :data="list" border fit highlight-current-row style="width: 100%; margin-top: -1px;">
      <el-table-column label="序列号" align="center" min-width="100">
        <template slot-scope="scope">
          <el-button type="text" class="serial-no-link" @click.stop="handleSerialClick(scope.row)">
            {{ serialDisplay(scope.row, scope.$index) }}
          </el-button>
        </template>
      </el-table-column>
      <el-table-column label="流程标题" min-width="160">
        <template slot-scope="{row}">
          <span>{{ row.flowTitle }}</span>
        </template>
      </el-table-column>
      <el-table-column label="发起人" width="100" align="center">
        <template slot-scope="{row}">
          <span>{{ row.creatorName }}</span>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="100" align="center">
        <template slot-scope="{row}">
          <el-tag :type="statusType(row.status)" size="small" effect="plain">{{ statusText(row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="发起时间" width="160" align="center">
        <template slot-scope="{row}">
          <span>{{ formatTime(row.createTime) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="当前节点" width="140" align="center">
        <template slot-scope="{row}">
          <span>{{ row.currentNodeTitle }}</span>
        </template>
      </el-table-column>
      <el-table-column label="待处理人" align="center" min-width="120" show-overflow-tooltip>
        <template slot-scope="{row}">
          <span>{{ row.pendingHandlerNames || '—' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" align="center" width="120" fixed="right">
        <template slot-scope="{row}">
          <el-button v-if="activeTab === 'todo'" type="primary" size="mini" icon="el-icon-s-check" @click="handleApprove(row)">{{ row.approveActionLabel || '审批' }}</el-button>
          <el-button v-else type="primary" size="mini" icon="el-icon-view" plain @click="handleView(row)">查看</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-wrap">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next"
        :total="total"
        :page-size.sync="pageSize"
        :current-page.sync="pageNum"
        :page-sizes="[10, 20, 50]"
        @size-change="getList"
        @current-change="getList"
      />
    </div>
  </div>
</template>

<script>
import { getTodoList, getDoneList, getMineList } from '../../api/workflow'
import { getYearRangePickerOptions, toCreateTimeRangeParams } from '../../utils'

export default {
  name: 'ApprovalCategory',
  data() {
    return {
      // 从路由 meta 或 query 获取流程编码，用于过滤
      flowCode: '',
      activeTab: 'todo',
      keyword: '',
      createTimeRange: null,
      loading: false,
      list: [],
      total: 0,
      pageNum: 1,
      pageSize: 10,
      todoCount: 0
    }
  },
  computed: {
    createTimePickerOptions() {
      return getYearRangePickerOptions()
    }
  },
  created() {
    // 流程编码优先级：query参数 > meta.flowCode > 路由名称（菜单编码）
    // 在菜单管理中，可以将 menu_code 设置为流程编码，如 seal_gaoguan_countersign
    this.flowCode = this.getFlowCode()
    this.getList()
    this.getTodoCount()
  },
  watch: {
    '$route'() {
      this.flowCode = this.getFlowCode()
      this.pageNum = 1
      this.getList()
      this.getTodoCount()
    }
  },
  methods: {
    getFlowCode() {
      // 优先级：路径参数 > query参数 > meta.flowCode
      // 在菜单管理中配置 path 为 /approval/category/seal_gaoguan_countersign
      // 其中 seal_gaoguan_countersign 就是流程编码
      return this.$route.params.flowCode || this.$route.query.flowCode || this.$route.meta.flowCode || ''
    },
    getList() {
      this.loading = true
      const params = {
        page: this.pageNum - 1,
        size: this.pageSize
      }
      if (this.keyword) params.keyword = this.keyword
      if (this.flowCode) params.flowCode = this.flowCode
      Object.assign(params, toCreateTimeRangeParams(this.createTimeRange))

      const apiMap = { todo: getTodoList, done: getDoneList, mine: getMineList }
      const apiFn = apiMap[this.activeTab] || getTodoList

      apiFn(params).then(res => {
        this.list = res.data.content || []
        this.total = res.data.totalElements || 0
        this.loading = false
      }).catch(() => { this.loading = false })
    },
    getTodoCount() {
      const params = { page: 0, size: 1 }
      if (this.flowCode) params.flowCode = this.flowCode
      getTodoList(params).then(res => {
        this.todoCount = res.data.totalElements || 0
        if (this.flowCode) this.$store.commit('approval/SET_ONE_TODO_COUNT', { flowCode: this.flowCode, count: this.todoCount })
      }).catch(() => {
        // ignore
      })
    },
    handleTabChange() {
      this.pageNum = 1
      this.getList()
    },
    handleSearch() {
      this.pageNum = 1
      this.getList()
    },
    handleStartFlow() {
      // 直接跳转到发起页面，带上流程编码
      this.$router.push({ path: '/approval/form', query: { flowCode: this.flowCode, mode: 'start' } })
    },
    handleApprove(row) {
      this.$router.push({ path: '/approval/form', query: { flowIncId: row.id, mode: 'approve' } })
    },
    handleView(row) {
      this.$router.push({ path: '/approval/form', query: { flowIncId: row.id, mode: 'view' } })
    },
    /** 业务单号优先，否则分页内顺序号（1 起） */
    serialDisplay(row, index) {
      const no = row && row.no != null ? String(row.no).trim() : ''
      if (no) return no
      return (this.pageNum - 1) * this.pageSize + index + 1
    },
    /** 与「审批」「查看」一致：待办进审批页，其余进查看页 */
    handleSerialClick(row) {
      if (this.activeTab === 'todo') {
        this.handleApprove(row)
      } else {
        this.handleView(row)
      }
    },
    statusType(s) {
      if (s === 'running') return 'primary'
      if (s === 'finished') return 'success'
      if (s === 'terminated') return 'danger'
      return 'info'
    },
    statusText(s) {
      if (s === 'running') return '进行中'
      if (s === 'finished') return '已完成'
      if (s === 'terminated') return '已终止'
      return s || '-'
    },
    formatTime(ts) {
      if (!ts) return '-'
      const d = new Date(ts)
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    }
  }
}
</script>

<style scoped>
.approval-category {
  padding: 16px;
}
.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.filter-area {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}
.pagination-wrap {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}
.tab-label-with-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.approval-todo-badge {
  margin-left: 2px;
  vertical-align: middle;
}
::v-deep .approval-todo-badge .el-badge__content {
  background-color: #f56c6c;
  color: #fff;
  border: none;
  height: 18px;
  min-width: 18px;
  padding: 0 5px;
  box-sizing: border-box;
  line-height: 18px;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  border-radius: 999px !important;
  transform: translateX(2px) translateY(0);
}
.serial-no-link {
  padding: 0;
  font-weight: 600;
  color: #409eff;
}
.serial-no-link:hover {
  color: #66b1ff;
}
</style>
